import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { clsx } from 'clsx';
import Loader from 'components/loader/loader.component';
import Button from 'components/button/button.component';
import Select from 'components/select/select.component';
import Icon from 'components/icon/icon.component';
import Pagination from 'components/pagination/pagination.component';
import { IObjectState } from 'interfaces/applicant.interface';
import {
  InventoryDailySummaryDto,
  InventoryDeliveryStatus,
  InventoryReportRowDto,
  InventoryReportStatsDto,
  InventorySeriesResult,
} from 'interfaces/report.interface';
import {
  getInventoryDailySummary,
  getInventoryReportRows,
  getInventoryReportStats,
  getInventorySeries,
} from 'requests/report.request';
import { getKpuState } from 'requests/history.request';
import {
  DateRangePreset,
  formatAge,
  formatDate,
  formatDateTime,
  formatMeasurement,
  formatNumber,
  formatPercent,
  isoToLocalInput,
  localInputToIso,
  presetRange,
  REPORT_TIME_ZONE,
} from 'utils/report.utils';
import StatTile from './components/stat-tile.component';
import StatusChip from './components/status-chip.component';
import VolumeChart from './components/volume-chart.component';
import './inventory-report.style.scss';

const PAGE_SIZES = [10, 20, 50, 100];
const PRESETS: DateRangePreset[] = ['today', 'yesterday', 'last7', 'last30', 'thisMonth'];
const STATUSES: InventoryDeliveryStatus[] = ['ACCEPTED', 'IN_FLIGHT', 'PENDING', 'FAILED'];

/** Table columns the backend can sort on, keyed by the JPA property path. */
const SORTABLE: Record<string, string> = {
  date: 'dateTimeUtc',
  object: 'tank.kpu.appObject.name',
  kpu: 'tank.kpu.name',
  // Not tank.name: the names end in "#1".."#36" and sort as text, so #10 lands
  // before #2. The Qoldau id is numeric and allocated in tank order.
  tank: 'tank.qoldauTankId',
  volume: 'volume',
  mass: 'mass',
  prodLevel: 'prodLevel',
  temp: 'temp',
  prodDensity: 'prodDensity',
};

const DEFAULT_SORT = 'dateTimeUtc,desc';

/** Shape the shared Select expects for its options. */
type Option = { id: string; name: string };

/** Filter values as held by the form controls. Committed to `applied` on Apply. */
type Filters = {
  from: string;
  to: string;
  appObjectId: string;
  kpuId: string;
  tankId: string;
  status: string;
};

const initialFilters = (): Filters => {
  const { from, to } = presetRange('today');
  return {
    from,
    to,
    appObjectId: '',
    kpuId: '',
    tankId: '',
    status: '',
  };
};

/** Filters live in the URL so a report can be bookmarked, shared and refreshed. */
const filtersFromUrl = (params: URLSearchParams): Filters => {
  const fallback = initialFilters();
  return {
    from: params.get('from') || fallback.from,
    to: params.get('to') || fallback.to,
    appObjectId: params.get('appObjectId') || '',
    kpuId: params.get('kpuId') || '',
    tankId: params.get('tankId') || '',
    status: params.get('status') || '',
  };
};

/**
 * Which preset, if any, the incoming filters correspond to. Without this a link
 * carrying custom dates still highlighted "Today" on load.
 */
const presetFromFilters = (filters: Filters): DateRangePreset | null => {
  const match = PRESETS.find((preset) => {
    const { from, to } = presetRange(preset);
    // `to` is "now" for most presets, so compare the start of the window and only
    // require the end to be close.
    return from === filters.from && Math.abs(Date.parse(to) - Date.parse(filters.to)) < 60_000;
  });
  return match ?? null;
};

const urlFromState = (filters: Filters, page: number, size: number, sort: string) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  if (page > 0) params.set('page', String(page));
  if (size !== 20) params.set('size', String(size));
  if (sort !== DEFAULT_SORT) params.set('sort', sort);
  return params;
};

const InventoryReportPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Draft is what the controls show; applied is what the last request used. Keeping
  // them apart stops half-typed filters leaking into a page change.
  const [draft, setDraft] = useState<Filters>(() => filtersFromUrl(searchParams));
  const [applied, setApplied] = useState<Filters>(() => filtersFromUrl(searchParams));

  const [page, setPage] = useState(() => Number(searchParams.get('page')) || 0);
  const [size, setSize] = useState(() => {
    const requested = Number(searchParams.get('size'));
    return PAGE_SIZES.includes(requested) ? requested : 20;
  });
  const [sort, setSort] = useState(() => searchParams.get('sort') || DEFAULT_SORT);

  const [rows, setRows] = useState<InventoryReportRowDto[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [daily, setDaily] = useState<InventoryDailySummaryDto[]>([]);
  const [stats, setStats] = useState<InventoryReportStatsDto | null>(null);
  const [seriesData, setSeriesData] = useState<InventorySeriesResult | null>(null);
  const [objects, setObjects] = useState<IObjectState[]>([]);

  const [loading, setLoading] = useState(false);
  const [activePreset, setActivePreset] = useState<DateRangePreset | null>(() =>
    presetFromFilters(filtersFromUrl(searchParams)),
  );

  const queryParams = useMemo(
    () => ({
      from: applied.from || undefined,
      to: applied.to || undefined,
      appObjectId: applied.appObjectId ? Number(applied.appObjectId) : undefined,
      kpuId: applied.kpuId ? Number(applied.kpuId) : undefined,
      tankId: applied.tankId ? Number(applied.tankId) : undefined,
      status: (applied.status || undefined) as InventoryDeliveryStatus | undefined,
    }),
    [applied],
  );

  useEffect(() => {
    setSearchParams(urlFromState(applied, page, size, sort), { replace: true });
  }, [applied, page, size, sort, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);

    // allSettled, not all: the three sections are independent, so a failure in the
    // summary or the stats must not blank out the rows the user came for.
    const [rowsResult, summaryResult, statsResult, seriesResult] = await Promise.allSettled([
      getInventoryReportRows({ ...queryParams, page, size, sort }),
      getInventoryDailySummary(queryParams),
      getInventoryReportStats(queryParams),
      getInventorySeries(queryParams),
    ]);

    if (rowsResult.status === 'fulfilled') {
      setRows(rowsResult.value.content);
      setTotalElements(rowsResult.value.totalElements);
    } else {
      setRows([]);
      setTotalElements(0);
    }

    setDaily(summaryResult.status === 'fulfilled' ? summaryResult.value : []);
    setStats(statsResult.status === 'fulfilled' ? statsResult.value : null);
    setSeriesData(seriesResult.status === 'fulfilled' ? seriesResult.value : null);

    const failed = [rowsResult, summaryResult, statsResult, seriesResult].filter(
      (r) => r.status === 'rejected',
    ).length;
    if (failed > 0) {
      toast.error(t('report.inventory.loadFailed'));
    }

    setLoading(false);
  }, [queryParams, page, size, sort, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    getKpuState()
      .then((res) => setObjects(res.objects ?? []))
      .catch(() => setObjects([]));
  }, []);

  // Cascading options: picking an object narrows the KPUs, which narrows the tanks.
  const objectOptions = useMemo<Option[]>(
    () => objects.map((o) => ({ id: String(o.id), name: o.objectName || `#${o.id}` })),
    [objects],
  );

  const kpuOptions = useMemo<Option[]>(() => {
    const scoped = draft.appObjectId
      ? objects.filter((o) => String(o.id) === draft.appObjectId)
      : objects;
    return scoped
      .flatMap((o) => o.kpus ?? [])
      .map((k) => ({ id: String(k.id), name: k.name || `KPU #${k.id}` }));
  }, [objects, draft.appObjectId]);

  const tankOptions = useMemo<Option[]>(() => {
    const scopedObjects = draft.appObjectId
      ? objects.filter((o) => String(o.id) === draft.appObjectId)
      : objects;
    return scopedObjects
      .flatMap((o) => o.kpus ?? [])
      .filter((k) => !draft.kpuId || String(k.id) === draft.kpuId)
      .flatMap((k) => k.tanks ?? [])
      .filter((tank) => typeof tank.id === 'number')
      .map((tank) => ({
        id: String(tank.id),
        name: tank.name || tank.deviceNum || `#${tank.id}`,
      }));
  }, [objects, draft.appObjectId, draft.kpuId]);

  const allOption = useMemo<Option>(() => ({ id: '', name: String(t('common.all')) }), [t]);

  /**
   * Colour slot per tank, derived from every configured tank rather than from
   * whatever the current filter returned - so narrowing the report never repaints
   * the tanks that remain. Tanks past the last slot are left off the chart instead
   * of reusing a hue.
   */
  const tankColorSlots = useMemo(() => {
    // Deliberately NOT sorted here: /front/kpu/state already returns objects, KPUs and
    // tanks in display order (KPU id, then Qoldau tank id). Re-sorting by internal id
    // would scramble the legend relative to every other list on the page.
    const allTankIds = objects
      .flatMap((o) => o.kpus ?? [])
      .flatMap((k) => k.tanks ?? [])
      .map((tank) => tank.id)
      .filter((id): id is number => typeof id === 'number');
    return new Map(allTankIds.map((id, index) => [id, index]));
  }, [objects]);

  const [sortField, sortDirection] = useMemo(() => {
    const [field, direction] = sort.split(',');
    return [field, direction === 'asc' ? 'asc' : 'desc'] as const;
  }, [sort]);

  const toggleSort = (columnKey: string) => {
    const field = SORTABLE[columnKey];
    if (!field) return;
    setPage(0);
    // First click on a new column sorts descending; clicking the active one flips it
    setSort(field === sortField && sortDirection === 'desc' ? `${field},asc` : `${field},desc`);
  };

  const setFilter = (key: keyof Filters, value: string) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      // Clear anything downstream that the new parent no longer contains
      if (key === 'appObjectId') {
        next.kpuId = '';
        next.tankId = '';
      }
      if (key === 'kpuId') {
        next.tankId = '';
      }
      return next;
    });
  };

  // Presets apply straight away. Only updating the draft made them look inert - the
  // inputs changed but nothing reloaded until Apply was pressed.
  const applyPreset = (preset: DateRangePreset) => {
    const { from, to } = presetRange(preset);
    const next = { ...draft, from, to };
    setActivePreset(preset);
    setPage(0);
    setDraft(next);
    setApplied(next);
  };

  const applyFilters = () => {
    setPage(0);
    setApplied(draft);
  };

  const resetFilters = () => {
    const fresh = initialFilters();
    setPage(0);
    setSort(DEFAULT_SORT);
    setActivePreset('today');
    setDraft(fresh);
    setApplied(fresh);
  };

  const acceptanceTone = useMemo(() => {
    if (!stats || !stats.totalReadings) return 'neutral' as const;
    const share = stats.accepted / stats.totalReadings;
    if (share >= 0.98) return 'positive' as const;
    if (share >= 0.9) return 'warning' as const;
    return 'critical' as const;
  }, [stats]);

  const hasNoTanks = objects.length > 0 && tankOptions.length === 0 && !draft.appObjectId;

  return (
    <Loader loading={loading}>
      <div className="wrapper report">
        <div className="report__header">
          <div>
            <h1 className="title">{t('report.inventory.title')}</h1>
            <span className="report__zoneNote">
              {t('report.inventory.timesShownIn', { zone: REPORT_TIME_ZONE })}
            </span>
          </div>
        </div>

        {stats && (
          <div className="report__stats">
            <StatTile
              label={t('report.inventory.stats.totalReadings')}
              value={formatNumber(stats.totalReadings, 0)}
              icon="Database"
            />
            <StatTile
              label={t('report.inventory.stats.accepted')}
              value={formatPercent(stats.accepted, stats.totalReadings)}
              hint={t('report.inventory.stats.acceptedOf', {
                accepted: formatNumber(stats.accepted, 0),
                total: formatNumber(stats.totalReadings, 0),
              })}
              icon="CircleCheck"
              tone={acceptanceTone}
            />
            <StatTile
              label={t('report.inventory.stats.failed')}
              value={formatNumber(stats.failed, 0)}
              hint={t('report.inventory.stats.pendingCount', {
                count: stats.pending + stats.inFlight,
              })}
              icon="CircleAlert"
              tone={stats.failed > 0 ? 'critical' : 'neutral'}
            />
            <StatTile
              label={t('report.inventory.stats.tanksReporting')}
              value={`${stats.tanksReporting} / ${stats.tanksConfigured}`}
              hint={
                stats.tanksReporting < stats.tanksConfigured
                  ? t('report.inventory.stats.silentTanks', {
                      count: stats.tanksConfigured - stats.tanksReporting,
                    })
                  : t('report.inventory.stats.allReporting')
              }
              icon="Gauge"
              tone={stats.tanksReporting < stats.tanksConfigured ? 'warning' : 'positive'}
            />
            <StatTile
              label={t('report.inventory.stats.lastReading')}
              value={formatAge(stats.lastReadingTime)}
              hint={formatDateTime(stats.lastReadingTime)}
              icon="Clock"
            />
          </div>
        )}

        <div className="report__presets">
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={clsx('report__preset', activePreset === preset && 'report__preset--active')}
              onClick={() => applyPreset(preset)}
              disabled={loading}
            >
              {t(`report.inventory.preset.${preset}`)}
            </button>
          ))}
        </div>

        <div className="report__filters">
          <label className="report__field">
            <span>{t('common.from')}</span>
            <input
              type="datetime-local"
              value={isoToLocalInput(draft.from)}
              // Clearing the field would otherwise send an open-ended range, which
              // scans millions of rows; fall back to the default window instead.
              onChange={(e) => {
                setActivePreset(null);
                setFilter('from', localInputToIso(e.target.value) ?? initialFilters().from);
              }}
            />
          </label>

          <label className="report__field">
            <span>{t('common.to')}</span>
            <input
              type="datetime-local"
              value={isoToLocalInput(draft.to)}
              onChange={(e) => {
                setActivePreset(null);
                setFilter('to', localInputToIso(e.target.value) ?? initialFilters().to);
              }}
            />
          </label>

          <div className="report__field">
            <span>{t('report.inventory.object')}</span>
            <Select
              name="appObjectId"
              hideNullOption
              value={draft.appObjectId}
              options={[allOption, ...objectOptions]}
              onChange={(e) => setFilter('appObjectId', e.target.value)}
            />
          </div>

          <div className="report__field">
            <span>{t('report.inventory.kpu')}</span>
            <Select
              name="kpuId"
              hideNullOption
              value={draft.kpuId}
              options={[allOption, ...kpuOptions]}
              onChange={(e) => setFilter('kpuId', e.target.value)}
            />
          </div>

          <div className="report__field">
            <span>{t('report.inventory.tank')}</span>
            <Select
              name="tankId"
              hideNullOption
              value={draft.tankId}
              options={[allOption, ...tankOptions]}
              onChange={(e) => setFilter('tankId', e.target.value)}
            />
          </div>

          <div className="report__field">
            <span>{t('report.inventory.statusLabel')}</span>
            <Select
              name="status"
              hideNullOption
              value={draft.status}
              options={[
                allOption,
                ...STATUSES.map((s) => ({
                  id: s as string,
                  name: String(t(`report.inventory.status.${s}`)),
                })),
              ]}
              onChange={(e) => setFilter('status', e.target.value)}
            />
          </div>

          <div className="report__field">
            <span>{t('common.pageSize')}</span>
            <Select
              name="size"
              hideNullOption
              value={String(size)}
              options={PAGE_SIZES.map((s) => ({ id: String(s), name: String(s) }))}
              onChange={(e) => {
                setPage(0);
                setSize(Number(e.target.value));
              }}
            />
          </div>

          <div className="report__filterActions">
            <Button onClick={applyFilters} disabled={loading} primary>
              {t('common.apply')}
            </Button>
            <Button onClick={resetFilters} disabled={loading} secondary>
              {t('common.reset')}
            </Button>
          </div>
        </div>

        <h2 className="report__sectionTitle">{t('report.inventory.chart.title')}</h2>
        <VolumeChart data={seriesData} colorSlots={tankColorSlots} />

        <h2 className="report__sectionTitle">{t('report.inventory.dailySummary')}</h2>
        {daily.length === 0 ? (
          <p className="report__empty">{t('common.noData')}</p>
        ) : (
          <div className="report__dailyGrid">
            {daily.map((d) => (
              <div className="report__dailyCard" key={`${d.tankId}-${d.day}`}>
                <div className="report__dailyHead">
                  <strong>{formatDate(d.day)}</strong>
                  <span>{d.tankName || `#${d.tankId}`}</span>
                </div>
                <div className="report__dailyBody">
                  <div>
                    <span className="report__muted">{t('report.inventory.avgVolume')}</span>
                    <strong>{formatNumber(d.avgVolume)}</strong>
                  </div>
                  <div>
                    <span className="report__muted">{t('report.inventory.minMax')}</span>
                    <strong>
                      {formatNumber(d.minVolume)} / {formatNumber(d.maxVolume)}
                    </strong>
                  </div>
                </div>
                <div className="report__dailyFoot">
                  {t('report.inventory.readings', { count: d.readingCount })} ·{' '}
                  {formatDateTime(d.lastReadingTime)}
                </div>
              </div>
            ))}
          </div>
        )}

        <h2 className="report__sectionTitle">{t('report.inventory.rows')}</h2>
        <div className="report__tableWrap">
          <table className="report__table">
            <thead>
              <tr>
                {[
                  'date',
                  'object',
                  'kpu',
                  'tank',
                  'product',
                  'volume',
                  'mass',
                  'prodLevel',
                  'temp',
                  'prodDensity',
                  'statusLabel',
                ].map((column) => {
                  const field = SORTABLE[column];
                  const active = field === sortField;
                  const label = String(t(`report.inventory.${column}`));
                  return (
                    <th key={column}>
                      {field ? (
                        <button
                          type="button"
                          className={clsx('report__sortBtn', active && 'report__sortBtn--active')}
                          onClick={() => toggleSort(column)}
                          title={String(t('report.inventory.sortBy', { column: label }))}
                        >
                          {label}
                          <Icon
                            name={
                              !active ? 'ChevronsUpDown' : sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'
                            }
                            size={13}
                          />
                        </button>
                      ) : (
                        label
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="report__emptyCell">
                    {hasNoTanks
                      ? t('report.inventory.noTanksConfigured')
                      : t('report.inventory.noRowsForFilter')}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id}>
                    <td className="report__cellDate">{formatDateTime(r.dateTimeUtc)}</td>
                    <td>{r.objectName || `#${r.appObjectId}`}</td>
                    <td>{r.kpuName || `#${r.kpuId}`}</td>
                    <td>
                      {r.tankName || `#${r.tankId}`}
                      {r.deviceNum && <span className="report__muted"> · {r.deviceNum}</span>}
                    </td>
                    <td>{r.prodName ?? '—'}</td>
                    <td>{formatMeasurement(r.volume, r.volumeUnitType, 'volumeUnitShort')}</td>
                    <td>{formatMeasurement(r.mass, r.massUnitType, 'massUnitShort')}</td>
                    <td>{formatMeasurement(r.prodLevel, r.levelUnitType, 'levelUnitShort')}</td>
                    <td>{formatNumber(r.temp, 1)}</td>
                    <td>{formatNumber(r.prodDensity, 3)}</td>
                    <td>
                      <StatusChip
                        status={r.status}
                        title={r.errorMessageWhenSend || r.errorMessageCheckStatus}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          totalItems={totalElements}
          itemsPerPage={size}
          currentPage={page}
          onPageChange={setPage}
          centered
        />
      </div>
    </Loader>
  );
};

export default InventoryReportPage;
